import { useEffect } from "react";
import Navigation from "@/components/Navigation";
import FooterAlt from "@/components/FooterAlt";
import StructuredData from "@/components/StructuredData";
import PortfolioSection from "@/components/home/PortfolioSection";
import { Button } from "@/components/ui/button";
import { setHeadMeta } from "@/lib/seo";

const journey = [
  {
    when: "Exited 2023",
    company: "Adfolks",
    what: "Founder & CEO. Scaled into a top Microsoft Azure partner in the Middle East.",
    outcome: "Acquired · ZainTech (Zain Group)",
  },
  {
    when: "Exited",
    company: "Actualize",
    what: "Voice agents that speak Arabic the way people in the GCC actually speak it.",
    outcome: "Acquired · CNTXT Tech",
  },
  {
    when: "Exited",
    company: "AppsIntegra",
    what: "Co-founder & Director. Enterprise datacenter and cloud automation.",
    outcome: "Exited",
  },
  {
    when: "Current",
    company: "AMX Ventures",
    what: "General Partner. Investing in AI infrastructure, sovereign & physical AI.",
    outcome: "SF ↔ Dubai",
  },
];

const withFounders = [
  {
    title: "Deal support",
    body: "Pricing, procurement, security reviews, and the CFO and CISO conversations that stall most enterprise AI deals.",
  },
  {
    title: "Market access",
    body: "Microsoft, AWS and GCP marketplaces, co-sell motions, and SI partnerships, built on the playbook that scaled Adfolks.",
  },
  {
    title: "Markets",
    body: "Scaling between the US and the GCC, in both directions, with buyers and partners on each side.",
  },
];

const Arun = () => {
  useEffect(() => {
    setHeadMeta({
      title: "Arun Mohan | General Partner, AMX Ventures | Exited Founder",
      description:
        "Arun Mohan is General Partner at AMX Ventures, an exited founder investing in AI infrastructure, sovereign & physical AI across the US and GCC. Connect with Arun.",
      canonical: "https://amx.ventures/arunmohan",
      ogTitle: "Arun Mohan | General Partner, AMX Ventures",
      ogDescription:
        "Exited founder turned investor. AI infrastructure, sovereign & physical AI, across the US and GCC.",
      ogType: "profile",
    });
  }, []);

  const personSchema = {
    "@context": "https://schema.org",
    "@type": "Person",
    "@id": "https://amx.ventures/arun#person",
    "name": "Arun Mohan",
    "jobTitle": "General Partner",
    "worksFor": {
      "@type": "Organization",
      "@id": "https://amx.ventures/#org",
      "name": "AMX Ventures",
      "url": "https://amx.ventures"
    },
    "url": "https://amx.ventures/arun",
    "email": "arun@amx.ventures",
    "description":
      "Exited founder turned General Partner at AMX Ventures. Invests in AI infrastructure, sovereign AI and physical AI, and works with portfolio companies as Investor & Operating Partner.",
    "knowsAbout": [
      "Venture Capital",
      "Enterprise B2B Sales",
      "AI Infrastructure",
      "Sovereign AI",
      "Physical AI",
      "Microsoft co-sell",
      "Go-to-Market Strategy"
    ],
    "homeLocation": [
      { "@type": "Place", "name": "San Francisco" },
      { "@type": "Place", "name": "Dubai" }
    ],
    "sameAs": ["https://www.linkedin.com/in/arunmohansf", "https://x.com/arunmohansf"]
  };

  return (
    <div className="min-h-screen">
      <StructuredData data={personSchema} />
      <Navigation />
      <main>
        {/* Hero */}
        <section className="px-4 py-32 bg-background">
          <div className="container mx-auto max-w-6xl space-y-8">
            <div className="inline-block px-6 py-3 bg-accent/10 border-2 border-accent/20 rounded">
              <span className="text-sm font-bold tracking-[0.3em] text-accent uppercase">
                General Partner · AMX Ventures
              </span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-12 items-center">
              <div className="space-y-8">
                <h1 className="text-5xl md:text-7xl font-black leading-tight">
                  <span className="text-primary">Arun Mohan.</span>
                  <br />
                  <span className="italic bg-gradient-to-r from-accent to-accent/70 bg-clip-text text-transparent">
                    Founder turned investor.
                  </span>
                </h1>
                <p className="text-xl md:text-2xl text-muted-foreground leading-relaxed max-w-3xl border-l-4 border-accent pl-6">
                  Before writing cheques, Arun spent 21 years founding, scaling, and exiting enterprise software
                  companies across San Francisco and Dubai. Now he invests in AI infrastructure, sovereign &amp;
                  physical AI, and works inside his portfolio as Investor &amp; Operating Partner.
                </p>
              </div>
              <img
                src="/arun-mohan.jpg"
                alt="Arun Mohan, General Partner at AMX Ventures"
                loading="eager"
                className="w-64 h-64 md:w-80 md:h-80 object-cover object-top border-4 border-accent shadow-2xl"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-6 pt-8">
              <div className="p-8 bg-secondary border-l-4 border-accent">
                <div className="text-5xl font-black text-accent mb-2">3</div>
                <p className="text-sm font-bold uppercase tracking-wider text-foreground">Exits</p>
              </div>
              <div className="p-8 bg-secondary border-l-4 border-primary">
                <div className="text-5xl font-black text-primary mb-2">21</div>
                <p className="text-sm font-bold uppercase tracking-wider text-foreground">
                  Years In Enterprise Deal Rooms
                </p>
              </div>
              <div className="p-8 bg-secondary border-l-4 border-accent">
                <div className="text-5xl font-black text-accent mb-2">50+</div>
                <p className="text-sm font-bold uppercase tracking-wider text-foreground">Enterprise Accounts</p>
              </div>
              <div className="p-8 bg-secondary border-l-4 border-primary">
                <div className="text-2xl font-black text-primary mb-2">Microsoft · AWS · GCP</div>
                <p className="text-sm font-bold uppercase tracking-wider text-foreground">Co-sell · SF ↔ Dubai</p>
              </div>
            </div>
          </div>
        </section>

        {/* The story */}
        <section className="px-4 py-32 bg-secondary border-y-4 border-foreground">
          <div className="container mx-auto max-w-4xl space-y-8">
            <div className="inline-block px-6 py-3 bg-accent/10 border-2 border-accent/20 rounded">
              <span className="text-sm font-bold tracking-[0.3em] text-accent uppercase">The Story</span>
            </div>
            <h2 className="text-4xl md:text-6xl font-black leading-tight text-foreground">
              First education: deal rooms, not classrooms
            </h2>
            <p className="text-xl text-foreground leading-relaxed">
              Arun's first education in enterprise software wasn't a classroom. It was deal rooms: hundreds of
              them, over 21 years, across San Francisco and Dubai.
            </p>
            <p className="text-xl text-foreground leading-relaxed">
              He founded and scaled Adfolks into one of the Middle East's top Microsoft Azure partners, then
              exited to ZainTech (Zain Group) in 2023. He exited Actualize, whose voice agents speak Arabic the
              way people in the GCC actually speak it, to CNTXT Tech. Earlier, he co-founded and exited
              AppsIntegra in enterprise datacenter and cloud automation.
            </p>

            <blockquote className="p-8 bg-background border-l-4 border-accent text-2xl md:text-3xl font-black leading-tight text-foreground">
              Enterprise buyers don't buy features. They buy risk reduction, clear ROI, and internal alignment.
            </blockquote>

            <p className="text-xl text-foreground leading-relaxed">
              Those years left him with that one conviction. Most founders learn it too late. Arun's job is to
              make sure his founders don't.
            </p>
            <p className="text-xl text-foreground leading-relaxed">
              Today he runs AMX Ventures, investing in AI infrastructure, sovereign &amp; physical AI, and
              working inside his portfolio on go-to-market, hyperscaler co-sell, procurement, and the boardroom
              conversations that actually close deals.
            </p>
          </div>
        </section>

        {/* The journey */}
        <section className="px-4 py-32 bg-background">
          <div className="container mx-auto max-w-6xl">
            <h2 className="text-4xl md:text-6xl font-black text-foreground mb-16">
              Founded, scaled, exited. Three times.
            </h2>
            <div className="space-y-6">
              {journey.map((row, i) => (
                <div
                  key={row.company}
                  className={`p-8 bg-secondary border-l-4 ${i % 2 === 0 ? "border-accent" : "border-primary"} grid md:grid-cols-12 gap-4 items-center hover:shadow-lg transition-shadow`}
                >
                  <div className="md:col-span-2">
                    <p className="text-sm font-bold uppercase tracking-wider text-muted-foreground">{row.when}</p>
                  </div>
                  <div className="md:col-span-2">
                    <p className="text-2xl font-black text-foreground">{row.company}</p>
                  </div>
                  <div className="md:col-span-5">
                    <p className="text-base text-foreground leading-relaxed">{row.what}</p>
                  </div>
                  <div className="md:col-span-3">
                    <p className="text-sm font-bold uppercase tracking-wider text-accent">{row.outcome}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <PortfolioSection />

        {/* With founders */}
        <section className="px-4 py-32 bg-primary text-primary-foreground">
          <div className="container mx-auto max-w-6xl">
            <h2 className="text-4xl md:text-6xl font-black text-primary-foreground mb-16">
              What working with Arun looks like
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {withFounders.map((item) => (
                <div key={item.title} className="space-y-3 border-l-4 border-accent pl-6 py-4">
                  <h3 className="text-2xl font-black tracking-wide text-primary-foreground">{item.title}</h3>
                  <p className="text-lg text-primary-foreground/90 leading-relaxed">{item.body}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Contact */}
        <section className="px-4 py-32 bg-background">
          <div className="container mx-auto max-w-4xl space-y-8">
            <h2 className="text-4xl md:text-6xl font-black text-foreground">Talk to Arun</h2>
            <p className="text-xl text-foreground leading-relaxed border-l-4 border-accent pl-6">
              Founders building in AI infrastructure, sovereign or physical AI. Enterprise leaders scaling AI.
              Ecosystem partners in the Microsoft/SI world. The inbox is open.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center">
              <Button size="lg" variant="default" className="text-lg h-14 px-10 font-bold" asChild>
                <a href="mailto:arun@amx.ventures">arun@amx.ventures</a>
              </Button>
              <div className="flex gap-6 text-sm font-bold uppercase tracking-wider">
                <a
                  href="https://www.linkedin.com/in/arunmohansf"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-foreground hover:text-accent transition-colors"
                >
                  LinkedIn
                </a>
                <a
                  href="https://x.com/arunmohansf"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-foreground hover:text-accent transition-colors"
                >
                  X (@arunmohansf)
                </a>
              </div>
            </div>
          </div>
        </section>
      </main>
      <FooterAlt />
    </div>
  );
};

export default Arun;